import { ReactElement, useState } from "react"
import moment from "moment"
import { CONTRIBUTOR_ROLE, HH_MODULE, formatDateTime } from "constants/constants"
import { XLSFormSchemaByID } from "interfaces/dataInterfaces"
import RenderRow from "components/Shared/Property/RenderRow"
import useCommon from "hooks/useCommon"
import { useUpdateBaseXlsHHMutation } from "services/household/xlsFormSchemas"
import { Blockquote, Button, TextInput } from "@wfp/ui"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

type Props = {
  data: XLSFormSchemaByID
  mode: string
  changeMode: () => void
}

const XlsFormDetails = ({ data, mode, changeMode }: Props): ReactElement => {
  const { showSnackBar } = useCommon()
  const user = useAppSelector(selectUser)

  const [updateXlsHH, { isLoading: isUpdating }] = useUpdateBaseXlsHHMutation()

  const [values, setValues] = useState<XLSFormSchemaByID>(data)

  const reset = (): void => {
    setValues(data)
    changeMode()
  }

  const handleEdit = async (): Promise<void> => {
    if (!values?.xlsFormName) {
      showSnackBar("Please fill xls form name", "warning")
      return
    }
    try {
      await updateXlsHH(values).unwrap()
      showSnackBar("Xls form edited successfully")
      changeMode()
    } catch (error) {
      const errorMessage =
        (error as { data: { message: string } }).data?.message ?? "Something went wrong, please try again"
      showSnackBar(errorMessage, "error")
    }
  }

  return (
    <div className="p-3">
      {!data?.isValid && (
        <Blockquote kind="error" withIcon className="mb-2">
          <p className="py-1">The XLSForm is invalid.</p>
        </Blockquote>
      )}
      {mode === "view" && (
        <table>
          <RenderRow label="Name" value={data?.xlsFormName} />
          <RenderRow label="Country" value={data?.countryName} />
          <RenderRow
            label="Base XLSForm"
            value={data?.baseXlsFormName}
            linkTo={`/household/baseXlsFormSchema/${data.xlsFormBaseId}#details`}
          />
          <RenderRow label="Category" value={data?.surveyCategoryName} />
          <RenderRow label="Type" value={data?.surveySubCategoryName} />
          <RenderRow label="Created by" value={data?.userName} />
          <RenderRow
            label="Created date"
            value={data?.xlsFormCreateDate ? moment(data.xlsFormCreateDate).format(formatDateTime) : ""}
          />
          <RenderRow
            label="Updated date"
            value={data?.xlsFormUpdateDate ? moment(data.xlsFormUpdateDate).format(formatDateTime) : ""}
          />
          <RenderRow label="Archived" value={data?.xlsFormsIsArchived ? "Yes" : "No"} />
        </table>
      )}

      {mode === "edit" && (
        <div className="space-y-5">
          <TextInput
            labelText="Name"
            name="name"
            placeholder="Name of the xls form schema"
            type="text"
            onChange={e => setValues(prev => ({ ...prev, xlsFormName: e.target.value }))}
            value={values?.xlsFormName}
            className="border-gray-300 rounded-md text-base w-50"
          />
        </div>
      )}

      <PermissionWrapper
        moduleId={HH_MODULE}
        subModule="xlsForms"
        feature="edit"
        exception={
          user?.roles?.find(role => role.applicationModuleId === HH_MODULE)?.roleName === CONTRIBUTOR_ROLE &&
          user?.nameid?.toLowerCase() === values?.userName?.toLowerCase()
        }
      >
        <div className="flex justify-between mt-4">
          <Button onClick={reset} kind={mode === "edit" ? "tertiary" : "primary"} disabled={isUpdating}>
            {mode === "edit" ? "Cancel" : "Edit"}
          </Button>
          {mode === "edit" && (
            <Button onClick={handleEdit} disabled={isUpdating}>
              Save
            </Button>
          )}
        </div>
      </PermissionWrapper>
    </div>
  )
}

export default XlsFormDetails
